import { Button, FlexBox, IconButton, Tooltip } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { isResponseWithData } from 'lib-shared-auth';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import type { MainConversation } from 'lib-shared-types-api';
import { MouseEventHandler, useCallback } from 'react';

import { conversationsApi } from 'apis/conversations';
import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { useNotifications } from 'hooks/useNotifications';
import { useCommandHandler } from 'utils/commands';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useCurrentUser } from 'utils/user';

import { ConversationPreviewCopilotDropdown } from '../copilotDropdown/conversationPreviewCopilotDropdown';

type ConversationPreviewActionsProps = {
  conversation: MainConversation;
  onNavigateToConversation?: MouseEventHandler<HTMLAnchorElement>;
};

export const ConversationPreviewActions = ({
  conversation,
  onNavigateToConversation,
}: ConversationPreviewActionsProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const { user } = useCurrentUser();

  const csid = conversation.id;
  const isAssigned = Boolean(conversation.assignment?.agent);
  const isAssignedToMe = user.id === conversation.assignment?.agent?.id;
  const { initial_channel: channel, direction } = conversation;
  const language =
    'language' in conversation.attributes
      ? conversation.attributes.language
      : undefined;

  const hasReplyAccess = useHasAccess('conversation.reply');
  const canClose =
    hasReplyAccess &&
    !conversation.is_closed &&
    (!isAssigned || isAssignedToMe);
  const canTransfer =
    hasReplyAccess &&
    conversation.initial_channel !== 'callback' &&
    (!isAssigned || isAssignedToMe);

  const handleClose = useCallback(async () => {
    const { isCancelled, isConfirmed, confirm } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Are you sure you want to close a conversation?',
            description: `This action will close conversation #${csid}.`,
            confirm: {
              variant: 'danger',
              label: 'Yes, close conversation',
              async: true,
              asyncLabel: 'Closing conversation...',
            },
          },
        },
      }),
    );

    if (isCancelled) {
      ampli.modalConversationCloseCancelClicked();
    }

    if (isConfirmed) {
      ampli.modalConversationCloseConfirmClicked({ csid });

      confirm(async () => {
        const response = await dispatch(
          conversationsApi.endpoints.updateConversationClose.initiate({ csid }),
        );

        if (isResponseWithData(response)) {
          ampli.conversationClosed({
            location: 'conversationsOverview',
            channel,
            language,
            direction,
          });

          notify({
            title: 'Conversation closed',
            text: `Conversation #${csid} was successfully closed`,
            icon: 'info',
            status: 'success',
            time: 3000,
          });
        } else {
          Sentry.captureException(response.error, {
            tags: {
              team: SentryTeam.Conversations,
            },
          });

          notify({
            title: 'Error while closing conversation',
            text: `Closing conversation ${csid} was not successful.`,
            icon: 'exclamation',
            status: 'warning',
          });
        }
      });
    }
  }, [channel, csid, direction, dispatch, language, notify]);

  const handleTransferToQueue = useCallback(() => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'app:conversationQueueTransfer',
          arg: {
            csid,
            isWarmTransferAvailable: false,
          },
        },
      }),
    );

    ampli.conversationsTransferToQueueClicked();
  }, [csid, dispatch]);

  useCommandHandler({
    name: 'conversation:close',
    actionFn: () => {
      if (canClose) {
        handleClose();
      }
    },
  });

  useCommandHandler({
    name: 'conversation:transferToQueue',
    actionFn: () => {
      if (canTransfer) {
        handleTransferToQueue();
      }
    },
  });

  return (
    <FlexBox alignItems="center" gap="200">
      {canClose && (
        <Tooltip
          content={
            <CommandKeybindingsTooltipContent commandName="conversation:close">
              Close conversation
            </CommandKeybindingsTooltipContent>
          }
        >
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Tooltip>
      )}

      {canTransfer && (
        <Tooltip
          content={
            <CommandKeybindingsTooltipContent commandName="conversation:transferToQueue">
              Transfer to queue
            </CommandKeybindingsTooltipContent>
          }
        >
          <Button variant="secondary" onClick={handleTransferToQueue}>
            Transfer to queue
          </Button>
        </Tooltip>
      )}

      <ConversationPreviewCopilotDropdown />

      <Tooltip
        content={
          <CommandKeybindingsTooltipContent commandName="conversation:goToConversation">
            Open conversation
          </CommandKeybindingsTooltipContent>
        }
      >
        <AppLink
          path="/conversation/:csid"
          params={{ path: { csid: `${csid}` } }}
          onClick={onNavigateToConversation}
          tabIndex={-1}
        >
          <IconButton
            aria-label="View conversation"
            icon="external-link"
            variant="secondary"
          />
        </AppLink>
      </Tooltip>
    </FlexBox>
  );
};
